import math from 'mathjs';

/**
 * DataFormat类定义
 */
class DataFormat {
    /**
     * 数值格式化逻辑
     * @param {Number} value 要格式化的数值
     * @param {Object | Number} formatOption 格式化选项，
     *      如果是Object，含有三个属性：
     *          less:（绝对值小于10部分）含有decimal和isFillZeroInFront属性
     *          more:（绝对值大于等于10部分）含有decimal属性
     *          isRemoveExtraZero:是否移去小数部分多余的0（默认值：true）
     *      如果是Number，则等效于:{less: {decimal: 传递的数值}, isRemoveExtraZero: true}
     * @returns {String} 返回格式化后的值，如果value不是数字，则返回原始值
     */
    numberFormatLogic(value, formatOption) {
        if (typeof value !== 'number') {
            return value;
        }

        switch (typeof formatOption) {
            case 'number':
                formatOption = {
                    less: {
                        decimal: formatOption,
                        isFillZeroInFront: false
                    }
                };
                break;
            case 'object':
                break;
            default:
                formatOption = {};
                break;
        }

        // 默认保留2位小数
        let decimal = 2;
        if (math.abs(value) < 10) {
            let lessOption = formatOption.less;
            if (!lessOption) {
                lessOption = {};
            }

            // 小于10默认保留8位小数
            decimal = 8;
            if (typeof lessOption.decimal === 'number') {
                decimal = lessOption.decimal;
            }
        } else {
            let moreOption = formatOption.more;
            if (!moreOption) {
                moreOption = {};
            }

            if (typeof moreOption.decimal === 'number') {
                decimal = moreOption.decimal;
            }
        }

        let formatValue = '';
        // math.js库的bug：如果保留0位小数，它不做任何处理，直接返回
        if (decimal === 0) {
            formatValue = math.round(value).toString();
        } else {
            // 因为JavaScript的小数位精度比较低，导致Number.toFixed方法四舍五入的时候值不对，因此改用第三方数字计算类
            formatValue = math.format(value, { notation: 'fixed', precision: decimal });
        }

        /**
         * 需求变更：去掉保留小数位数后，小数部分多余的0
         * 申请人员：王嘉利
         * 申请日期：2018-07-16
         */
        if (decimal > 0 && formatOption.isRemoveExtraZero !== false) {
            formatValue = formatValue.replace(/([^0])0*$/, '$1');
            // 如果去掉末尾的0之后，只剩“.”，则删除掉
            formatValue = formatValue.replace(/\.$/, '');
        }

        if (value < 10 && formatOption.less && formatOption.less.isFillZeroInFront) {
            formatValue = '0' + formatValue;
        }
        return formatValue;
    }

    /**
     * 百分比格式化逻辑
     * @param {Number} value 要格式化的值
     * @returns {String} 返回格式化后的值
     */
    percentFormatLogic(value) {
        if (typeof value !== 'number') {
            value = 0;
        }

        value *= 100;
        let symbol = '';
        if (value > 0) {
            symbol = '+';
        }
        return symbol + value.toFixed(2) + '%';
    }

    /**
     * 日期时间格式化逻辑
     * @param {Number} value 时间戳
     * @param {String} type 返回的日期时间类型(如：1m，1d等，如果不提供，则返回日期+时间)
     * @returns {String} 返回格式化后的值
     */
    datetimeFormatLogic(value, type = '') {
        let date = new Date(value);
        if (!isFinite(date)) {
            return '';
        }
        let dates = [];
        dates.push(date.getFullYear());
        dates.push(date.getMonth() + 1);
        dates.push(date.getDate());
        // 小于10则补零
        dates = dates.map(date => {
            return this.numberFormatLogic(date, {
                less: { decimal: 0, isFillZeroInFront: true },
                more: { decimal: 0 }
            });
        });
        let times = [];
        times.push(date.getHours());
        times.push(date.getMinutes());
        times.push(date.getSeconds());
        // 小于10则补零
        times = times.map(time => {
            return this.numberFormatLogic(time, {
                less: { decimal: 0, isFillZeroInFront: true },
                more: { decimal: 0 }
            });
        });

        switch (type.toString().slice(-1)) {
            case 'd':
                return `${dates.join('/')}`;
            case 'm':
                return `${times.join(':')}`;
            default:
                return `${dates.join('/')} ${times.join(':')}`;
        }
    }

    /**
     * 成交量格式化逻辑
     * @param {Number} volume 市值
     * @returns {String} 返回格式化后的值
     */
    volumeFormatLogic(volume) {
        let unit = '亿';
        let divisor = 100000000;
        let value = math.abs(volume);
        if (value < divisor) {
            unit = '万';
            divisor /= 10000;
        }
        if (value < divisor) {
            // unit = 'k';
            // divisor /= 10;
            unit = '';
            divisor = 1;
        }

        value = math.divide(volume, divisor);
        let formatValue = math.format(value, { notation: 'fixed', precision: 2 });
        // 去掉末尾多余的0
        formatValue = formatValue.replace(/([^0])0*$/, '$1');
        // 如果是.00，在去掉末尾的0之后，只剩“.”，则删除掉
        formatValue = formatValue.replace(/\.$/, '');
        return formatValue + unit;
    }
}

export default new DataFormat();



// WEBPACK FOOTER //
// ./src/controller/common/dataFormat.js